import React, { useState, useRef, useEffect } from "react";
import { T } from "../theme";   // Theme tokens (navy palette)
import { TEMPLATES, CATALOG } from "../data/catalog";
import {
  makeRectShop,
  makeFreeShop,
  computeBbox,
  polygonArea,
  polygonPerimeter,
  isSelfIntersecting,
  getWallEndpoints,
  getDoorEndpoints,
  makeDoorOnWall,
} from "../utils/shopGeometry";

// ─── Architectural drawing colors (semantic, NOT themed) ───
// These match the conventions of the CAD canvas itself — keep in sync.
const ARCH = {
  wall:     "#00ccff",   // cyan walls
  walkDoor: "#00cc88",   // green
  ovrDoor:  "#ffcc00",   // yellow
  dim:      "#ff9900",   // orange dimension lines
  north:    "#ff4444",   // red N marker
  cardinal: "#00ccff",   // S/E/W markers (cyan to match walls)
  floorBg:  "#030c18",   // very dark blue floor (matches CadCanvas)
  gridLine: "#0c2236",
  vertex:   "#185FA5",   // wall corner dots
  vStart:   "#1D9E75",   // green start vertex (drawing mode)
  vSelect:  "#ffcc00",   // yellow when selected
  guide:    "#ff6b9d",   // pink alignment guide
  preview:  "#00ccff",   // cyan preview line
  freeAng:  "#ff9900",   // orange when shift-held (free angle)
  warn:     "#ff4444",   // red warning
};

// ─── Free-form drawing constants ───
// Drawing area is generous so real shops (often with wings/extensions) fit
// without feeling cramped. Grid + clickable area fill the whole viewBox so
// users aren't surprised by clicks "outside" the visible grid.
//
// Sized to comfortably fit shops up to ~200 ft on the long side (most automotive
// shops, even with wings, fall well under this). The canvas supports wheel-zoom
// and middle/Alt-drag pan, plus a "Fit" button — so small shops can zoom in for
// detailed work and large shops can zoom out to see the whole drawing.
const DRAW_VIEWBOX_W = 220;         // ft, the maximum drawable area width
const DRAW_VIEWBOX_H = 160;         // ft, the maximum drawable area height
// Default view at 1x zoom — small enough that small shops feel comfortable.
// Users can zoom out to see the full 220×160 drawing area or zoom in for detail.
const DEFAULT_VIEW_W = 120;         // ft
const DEFAULT_VIEW_H = 80;          // ft
const ZOOM_MIN = 0.25;              // zoomed out: each ft is 1/4 its default px size
const ZOOM_MAX = 6;                 // zoomed in: each ft is 6× its default px size
const ZOOM_STEP = 1.12;             // per wheel tick, matches CadCanvas
const MIN_HIT_PX = 10;              // minimum vertex/wall hit area in screen pixels
const FIT_PADDING_FT = 6;           // padding around drawing when auto-fitting
const SNAP_ANGLES = [0, 45, 90, 135, 180, 225, 270, 315];
const ANGLE_SNAP_TOL = 7;           // degrees
const ALIGN_TOL_FT = 1.0;           // feet — alignment guide snap distance
const VERTEX_HIT_FT = 1.5;          // feet — radius to grab a vertex
const CLOSE_THRESHOLD_FT = 2;       // feet — radius to close shape near start
const FOOT_SNAP = 1;                // feet — quantize mouse to whole foot increments (Alt to disable)

export default function SetupPage({ shop, setShop, doors, setDoors, form, setForm, onPickTemplate }) {
  // Update a single form field — mirrors the pattern used in ExportPage so the
  // same `form` object stays in sync across both pages.
  const updateField = (key) => (e) => {
    const value = e && e.target ? e.target.value : e;
    setForm((prev) => ({ ...prev, [key]: value }));
  };
  // Which wall is currently selected in the doors panel (rect mode uses cardinal direction)
  const [activeWall, setActiveWall] = useState("N");
  // For free-form mode: which wall the user clicked to add/edit doors (wallId number)
  const [selectedWallId, setSelectedWallId] = useState(null);
  // For free-form mode: are we in "edit walls" mode (re-entered drawing for a closed shape)?
  // When true: the user can hover walls to see ✕ delete buttons. When a wall is deleted,
  // the polygon becomes open and the user must close it again before they can leave edit
  // mode or proceed to the floor plan. This keeps the data model simple (one gap at a time).
  const [editMode, setEditMode] = useState(false);

  // Current shape mode — pulled from shop so it persists across renders/saves
  const shapeType = shop.shapeType || "rect";

  // Switch shape mode. If switching to free-form from rect, start with empty drawing.
  // If switching from free-form back to rect with a drawn shape, confirm first since
  // we can't preserve polygon data inside a rectangle.
  const switchShapeMode = (newMode) => {
    if (newMode === shapeType) return;
    if (newMode === "rect" && shapeType === "free" && shop.vertices && shop.vertices.length >= 3) {
      if (!window.confirm("Switching to Rectangle will discard your custom shape. Continue?")) return;
    }
    // Doors are tied to specific wallIds, which don't carry over between
    // rectangle (0-3) and free-form (varies) shapes. Clear them on switch
    // so we don't end up with orphan doors pointing at nonexistent walls.
    if (newMode === "rect") {
      setShop(makeRectShop(60, 40, shop.unit || "ft"));
      setDoors([]);
    } else {
      // Start free-form with no vertices yet — user draws the shape
      setShop({
        shapeType: "free",
        w: 0, h: 0, unit: shop.unit || "ft",
        vertices: [],
        walls: [],
      });
      setDoors([]);
    }
    // Reset edit mode in case user was mid-edit when they switched.
    setEditMode(false);
  };

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        overflow: "hidden",
        background: T.appBg,           // matches App.js shell
        fontFamily: T.font,
      }}
    >
      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* LEFT — config panel                                              */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <div
        style={{
          width: 420,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          background: T.panelRaised,
          borderRight: T.hairline,
          overflow: "auto",
        }}
      >
        {/* Header section */}
        <div style={{ padding: "20px 22px", borderBottom: T.hairline }}>
          <div style={{ fontSize: 18, fontWeight: 600, color: T.text }}>
            Set up your shop
          </div>
          <div style={{ fontSize: 13, color: T.textDim, marginTop: 4, lineHeight: 1.5 }}>
            Pick a template to start fast, or enter your own size and doors.
          </div>
        </div>

        {/* ─── Start from a template ───────────────────────────────── */}
        {/* The 4 TEMPLATES in catalog.js (shop + doors + equipment). Picking
            one jumps straight to the floor plan via App.applyTemplate. */}
        {onPickTemplate && (
          <div style={{ padding: "14px 22px", borderBottom: T.hairline }}>
            <SectionLabel>Start from a template</SectionLabel>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 10 }}>
              {TEMPLATES.map((t) => (
                <TemplateCard key={t.id} template={t} onPick={() => onPickTemplate(t)} />
              ))}
            </div>
            <div style={{ fontSize: 12, color: T.textDim, marginTop: 10 }}>
              Or set up a blank shop below — you can change everything later.
            </div>
          </div>
        )}

        {/* ─── Project Information ─────────────────────────────────── */}
        {/* Single source of truth for project name — flows through to the
            Export page automatically since `form` is lifted to App.js.
            Placed at top because it's the first thing a user should set. */}
        <div style={{ padding: "14px 22px", borderBottom: T.hairline }}>
          <SectionLabel>Project Information</SectionLabel>
          <div style={{ marginTop: 10 }}>
            <FieldLabel>Project Name</FieldLabel>
            <input
              type="text"
              value={(form && form.projectName) || ""}
              onChange={updateField("projectName")}
              placeholder="New Service Bay - Phase 1"
              style={inputS}
            />
          </div>
        </div>

        {/* ─── Shape mode toggle (NEW) ─────────────────────────────── */}
        {/* Default is Rectangle. Free-form lets users draw irregular shapes
            with angled walls — useful for buildings that aren't simple boxes. */}
        <div style={{ padding: "14px 22px", borderBottom: T.hairline }}>
          <SectionLabel>Shop Shape</SectionLabel>
          <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
            <button
              onClick={() => switchShapeMode("rect")}
              style={shapeBtnS(shapeType === "rect")}
            >
              ▭ Rectangle
            </button>
            <button
              onClick={() => switchShapeMode("free")}
              style={shapeBtnS(shapeType === "free")}
            >
              ⬡ Free-form
            </button>
          </div>
        </div>

        {/* Display preferences (wall measurements, door labels) moved out of
            Setup — they now live in the canvas bottom-bar "Labels" drop-up so
            users can toggle them in-place while editing. Underlying state on
            shop.showWallMeasurements / shop.showDoorLabels is unchanged. */}

        {/* ─── Rectangle mode controls ─────────────────────────────── */}
        {shapeType === "rect" && (
          <RectControls
            shop={shop}
            setShop={setShop}
            doors={doors}
            setDoors={setDoors}
            activeWall={activeWall}
            setActiveWall={setActiveWall}
          />
        )}

        {/* ─── Free-form mode controls ─────────────────────────────── */}
        {shapeType === "free" && (
          <FreeFormControls
            shop={shop}
            setShop={setShop}
            doors={doors}
            setDoors={setDoors}
            selectedWallId={selectedWallId}
            setSelectedWallId={setSelectedWallId}
            editMode={editMode}
            setEditMode={setEditMode}
          />
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* RIGHT — preview area                                             */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          padding: "20px 24px",
          gap: 12,
          minWidth: 0,
          minHeight: 0,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: T.textDim,
            letterSpacing: ".1em",
            textTransform: "uppercase",
            textAlign: "center",
            flexShrink: 0,
          }}
        >
          {shapeType === "rect" ? "Shop Preview" : "Draw Your Shop"}
        </div>

        {/* Preview/drawing canvas — switches based on shape mode */}
        <div
          style={{
            flex: 1,
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 0,
          }}
        >
          {shapeType === "rect" ? (
            <RectPreview shop={shop} doors={doors} />
          ) : (
            <FreeFormCanvas
              shop={shop}
              setShop={setShop}
              doors={doors}
              setDoors={setDoors}
              selectedWallId={selectedWallId}
              setSelectedWallId={setSelectedWallId}
              editMode={editMode}
              setEditMode={setEditMode}
            />
          )}
        </div>

        {/* Legend below preview (only meaningful for rect mode for now) */}
        {shapeType === "rect" && (
          <div
            style={{
              display: "flex",
              gap: 18,
              justifyContent: "center",
              fontSize: 11,
              color: T.textDim,
              flexShrink: 0,
            }}
          >
            <span style={{ color: ARCH.walkDoor }}>━ Walk Door</span>
            <span style={{ color: ARCH.ovrDoor }}>━ Overhead Door</span>
          </div>
        )}

        {/* The old big "Open Floor Plan" button lived here; the top bar's
            stepper / "Next: Floor Plan" button replaces it (same validation,
            computed in App.js as canLeaveSetup). */}

        {/* Helper text */}
        <div
          style={{
            fontSize: 11,
            color: T.textMuted,
            textAlign: "center",
            lineHeight: 1.7,
          }}
        >
          Rooms, equipment and floor drains are added on the floor plan.
          <br />
          When you're ready, click <b style={{ color: T.text }}>Next: Floor Plan</b> at the top right.
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// RECTANGLE MODE — sidebar controls (preserves original behavior)
// ═════════════════════════════════════════════════════════════════════
function RectControls({ shop, setShop, doors, setDoors, activeWall, setActiveWall }) {
  // When the user types a new width/height in rect mode, we rebuild the shop
  // object via makeRectShop() so the vertices/walls fields stay in sync.
  // (Previously this was a simple spread; now we also need derived fields.)
  const updateDim = (field, value) => {
    setShop((s) => makeRectShop(
      field === "w" ? +value : s.w,
      field === "h" ? +value : s.h,
      s.unit
    ));
  };
  // Normalize any legacy "m" project back to feet (see Unit field note below)
  useEffect(() => {
    if (shop.unit && shop.unit !== "ft") setShop((s) => ({ ...s, unit: "ft" }));
  }, [shop.unit, setShop]);

  // Add a door to a wall at its midpoint. We use the new wall-attached format
  // (wallId + offset in feet). Cardinal direction maps to wallId via shop.walls.
  // Message shown when a door can't be placed (wall full or too short).
  // Cleared on the next successful add or when the user switches walls.
  const [doorWarning, setDoorWarning] = useState("");

  const addDoor = (wallDirection, type) => {
    const wall = shop.walls?.find((w) => w.direction === wallDirection);
    if (!wall) return;
    const newDoor = makeDoorOnWall(shop, wall.id, type, doors);
    if (!newDoor) {
      // makeDoorOnWall returns null when no gap on the wall fits this opening.
      setDoorWarning(
        `No room for ${type === "overhead" ? "a 10ft overhead" : "a 4ft walk"} door on this wall — remove or move one first.`
      );
      return;
    }
    setDoorWarning("");
    setDoors((prev) => [...prev, newDoor]);
  };
  const removeDoor = (id) => setDoors((prev) => prev.filter((d) => d.id !== id));

  // Helper: given a cardinal direction, return all doors on that wall.
  // Uses wallId to filter (the new format) so this works after migration.
  const doorsForWall = (wallDirection) => {
    const wall = shop.walls?.find((w) => w.direction === wallDirection);
    if (!wall) return [];
    return doors.filter((d) => d.wallId === wall.id);
  };

  return (
    <>
      {/* Dimensions section */}
      <div style={{ padding: "18px 22px", borderBottom: T.hairline }}>
        <SectionLabel>Shop Dimensions</SectionLabel>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 120px",
            gap: 10,
            marginTop: 10,
          }}
        >
          <div>
            <FieldLabel>Width</FieldLabel>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <DimInput value={shop.w} onCommit={(v) => updateDim("w", v)} />
              <span style={{ color: T.textDim, fontSize: 11 }}>ft</span>
            </div>
          </div>
          <div>
            <FieldLabel>Depth</FieldLabel>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <DimInput value={shop.h} onCommit={(v) => updateDim("h", v)} />
              <span style={{ color: T.textDim, fontSize: 11 }}>ft</span>
            </div>
          </div>
          <div>
            <FieldLabel>Unit</FieldLabel>
            {/* Metres removed for now: equipment sizes, snapping and labels are all
                in feet, so picking "m" only relabelled the numbers (a 60x40 ft shop
                became "60x40 m" with lifts still drawn in feet). Re-add once there's
                a real display conversion. Old .sbpro files with unit "m" get fixed
                to "ft" by the effect below. */}
            <div style={{ ...inputS, color: T.textDim, cursor: "default" }}>Feet</div>
          </div>
        </div>
      </div>

      {/* Doors section */}
      <div style={{ padding: "16px 22px", borderBottom: T.hairline }}>
        <SectionLabel>Doors & Openings</SectionLabel>

        {/* Wall selector tabs */}
        <div style={{ display: "flex", gap: 4, marginTop: 10, marginBottom: 10 }}>
          {["N", "S", "E", "W"].map((w) => {
            const isActive = activeWall === w;
            const wallDoorCount = doorsForWall(w).length;
            return (
              <button
                key={w}
                onClick={() => { setActiveWall(w); setDoorWarning(""); }}
                style={{
                  flex: 1,
                  background: isActive ? T.blueSoft : "transparent",
                  border: `1px solid ${isActive ? T.blue + "66" : "rgba(255,255,255,0.08)"}`,
                  borderRadius: T.radiusSm,
                  color: isActive ? T.blue : T.textDim,
                  fontSize: 11,
                  fontWeight: 600,
                  padding: "6px 0",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  letterSpacing: ".05em",
                  transition: "all .12s",
                  position: "relative",
                }}
              >
                {w === "N" ? "NORTH" : w === "S" ? "SOUTH" : w === "E" ? "EAST" : "WEST"}
                {wallDoorCount > 0 && (
                  <span style={{ marginLeft: 5, fontSize: 11, opacity: 0.7 }}>
                    ({wallDoorCount})
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Add buttons */}
        <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
          <button onClick={() => addDoor(activeWall, "door")} style={{ ...doorBtnS(ARCH.walkDoor), flex: 1 }}>
            + Walk Door
          </button>
          <button onClick={() => addDoor(activeWall, "overhead")} style={{ ...doorBtnS(ARCH.ovrDoor), flex: 1 }}>
            + Overhead
          </button>
        </div>

        {/* Shown only when the last add was refused for lack of space */}
        {doorWarning && (
          <div style={{ fontSize: 11, color: ARCH.warn, marginBottom: 8, lineHeight: 1.4 }}>
            {doorWarning}
          </div>
        )}

        {/* Door list for active wall — uses doorsForWall() which filters by wallId */}
        {doorsForWall(activeWall).length > 0 ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {doorsForWall(activeWall).map((d) => {
              const isOvr = d.type === "overhead";
              const clr = isOvr ? ARCH.ovrDoor : ARCH.walkDoor;
              return (
                <div
                  key={d.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    background: `${clr}15`,
                    border: `1px solid ${clr}44`,
                    borderRadius: 4,
                    padding: "3px 8px",
                    fontSize: 11,
                    color: clr,
                  }}
                >
                  {isOvr ? `OVR ${d.width}ft` : "WLK"}
                  <button
                    onClick={() => removeDoor(d.id)}
                    style={{
                      background: "none", border: "none", color: "inherit",
                      cursor: "pointer", fontSize: 11, padding: 0, opacity: 0.7,
                    }}
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ fontSize: 11, color: T.textMuted, fontStyle: "italic" }}>
            No doors on this wall yet.
          </div>
        )}
      </div>
    </>
  );
}

// ═════════════════════════════════════════════════════════════════════
// FREE-FORM MODE — sidebar controls
// ═════════════════════════════════════════════════════════════════════
function FreeFormControls({ shop, setShop, doors, setDoors, selectedWallId, setSelectedWallId, editMode, setEditMode }) {
  const verts = shop.vertices || [];
  const closed = verts.length >= 3 && shop.walls && shop.walls.length === verts.length;
  const intersecting = closed && isSelfIntersecting(verts);
  const area = closed ? polygonArea(verts) : 0;
  const perim = verts.length >= 2 ? polygonPerimeter(closed ? verts : verts) : 0;

  // Reset the drawing — clears all vertices and goes back to "draw from scratch"
  const resetShape = () => {
    if (verts.length > 0 && !window.confirm("Discard the current shape and start over?")) return;
    setShop((s) => ({ ...s, vertices: [], walls: [], w: 0, h: 0 }));
    if (setEditMode) setEditMode(false);
  };

  // Delete the most recently added vertex (one-step undo for drawing)
  const undoLastVertex = () => {
    if (verts.length === 0) return;
    setShop((s) => {
      const newVerts = s.vertices.slice(0, -1);
      const bbox = computeBbox(newVerts);
      return { ...s, vertices: newVerts, walls: [], w: Math.round(bbox.w * 10) / 10, h: Math.round(bbox.h * 10) / 10 };
    });
  };

  // Enter wall-edit mode. User can hover walls to see ✕ delete buttons. After
  // deleting any wall the polygon becomes open and they must close it again
  // before they can exit edit mode or proceed.
  const enterEditMode = () => {
    if (setEditMode) setEditMode(true);
  };
  // Exit wall-edit mode. Only allowed if the polygon is currently closed
  // (otherwise we'd leave the user with a half-open shape they can't proceed from).
  const exitEditMode = () => {
    if (!closed) return;
    if (setEditMode) setEditMode(false);
  };

  return (
    <>
      {/* Drawing instructions */}
      <div style={{ padding: "16px 22px", borderBottom: T.hairline }}>
        <SectionLabel>Drawing Tool</SectionLabel>
        <div
          style={{
            marginTop: 10,
            padding: "10px 12px",
            background: T.blueSoft,
            border: `1px solid ${T.blue}33`,
            borderRadius: T.radiusSm,
            fontSize: 11,
            color: T.textDim,
            lineHeight: 1.6,
          }}
        >
          {!closed ? (
            <>
              {editMode && (
                <div style={{ marginBottom: 8, padding: "6px 8px", background: `${ARCH.warn}15`, border: `1px solid ${ARCH.warn}55`, borderRadius: T.radiusSm, color: ARCH.warn, fontSize: 11, fontWeight: 600 }}>
                  Edit mode — close the shape to finish editing
                </div>
              )}
              Click on the canvas to place wall corners. Mouse snaps to whole feet
              (<b>Alt</b> for fine positioning). Walls snap to <b>0/45/90°</b>
              (<b>Shift</b> for any angle).<br /><br />
              <b style={{ color: T.blue }}>Type a number</b> to enter an exact wall
              length — the wall is placed in your cursor direction.<br /><br />
              Pink dashed lines show alignment with other corners. Click the{" "}
              <span style={{ color: ARCH.vStart }}>green dot</span> to close the shape.<br /><br />
              <span style={{ color: T.textMuted }}>Hover any placed wall and click <b style={{ color: ARCH.warn }}>✕</b> to delete it.</span>
            </>
          ) : (
            <>
              <b style={{ color: T.blue }}>Shape closed!</b>
              {editMode ? (
                <> Hover any wall and click <b style={{ color: ARCH.warn }}>✕</b> to delete it. Walls with doors can't be deleted — remove the door first.</>
              ) : (
                <> Drag corners to adjust, or click <b>Edit Walls</b> below to delete or rework walls.</>
              )}
            </>
          )}
        </div>

        {/* Edit Walls button — visible when polygon is closed (in either
            normal closed mode OR in edit mode after re-closing a gap). Hidden
            when polygon is open during edit-mode bridging — replaced by a
            prompt that tells the user to close the shape first. */}
        {closed && (
          <button
            onClick={editMode ? exitEditMode : enterEditMode}
            style={{
              ...secondaryBtnS,
              width: "100%",
              marginTop: 10,
              background: editMode ? `${ARCH.warn}22` : "transparent",
              borderColor: editMode ? `${ARCH.warn}88` : undefined,
              color: editMode ? ARCH.warn : T.text,
              fontWeight: 600,
            }}
          >
            {editMode ? "✓ Done Editing" : "✎ Edit Walls"}
          </button>
        )}
        {/* When polygon is open during edit mode, prompt user to close first
            (button above is hidden because it's gated on `closed`). */}
        {!closed && editMode && (
          <div
            style={{
              marginTop: 10,
              padding: "8px 10px",
              background: `${ARCH.warn}15`,
              border: `1px solid ${ARCH.warn}55`,
              borderRadius: T.radiusSm,
              color: ARCH.warn,
              fontSize: 11,
              textAlign: "center",
              fontWeight: 600,
            }}
          >
            Close the shape to finish editing
          </div>
        )}

        {/* Undo last + Reset buttons */}
        <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
          <button
            onClick={undoLastVertex}
            disabled={verts.length === 0 || closed}
            style={{
              ...secondaryBtnS,
              flex: 1,
              opacity: (verts.length === 0 || closed) ? 0.4 : 1,
              cursor: (verts.length === 0 || closed) ? "not-allowed" : "pointer",
            }}
          >
            ← Undo last corner
          </button>
          <button
            onClick={resetShape}
            disabled={verts.length === 0}
            style={{
              ...secondaryBtnS,
              flex: 1,
              opacity: verts.length === 0 ? 0.4 : 1,
              cursor: verts.length === 0 ? "not-allowed" : "pointer",
            }}
          >
            ⟲ Reset
          </button>
        </div>
      </div>

      {/* Shape stats */}
      <div style={{ padding: "16px 22px", borderBottom: T.hairline }}>
        <SectionLabel>Shape Stats</SectionLabel>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 10 }}>
          <StatCard label="Walls" value={closed ? verts.length : Math.max(0, verts.length - 1)} />
          <StatCard label="Perimeter" value={`${Math.round(perim)} ${shop.unit}`} />
          <StatCard label="Area" value={`${Math.round(area).toLocaleString()} sq${shop.unit}`} />
        </div>

        {/* Validation warning */}
        {intersecting && (
          <div
            style={{
              marginTop: 10,
              padding: "8px 10px",
              background: `${ARCH.warn}15`,
              border: `1px solid ${ARCH.warn}66`,
              borderRadius: T.radiusSm,
              fontSize: 11,
              color: ARCH.warn,
              lineHeight: 1.5,
            }}
          >
            ⚠ Walls cross each other. Drag corners to fix before continuing.
          </div>
        )}
      </div>

      {/* Doors panel — only enabled when shape is closed.
          Click a wall in the canvas to select it, then add walk/overhead doors. */}
      <div style={{ padding: "16px 22px", borderBottom: T.hairline }}>
        <SectionLabel>Doors & Openings</SectionLabel>
        {!closed ? (
          <div
            style={{
              marginTop: 10,
              padding: "10px 12px",
              background: T.surface,
              border: T.hairline,
              borderRadius: T.radiusSm,
              fontSize: 11,
              color: T.textMuted,
              lineHeight: 1.6,
              fontStyle: "italic",
            }}
          >
            Close the shape first to start placing doors.
          </div>
        ) : (
          <FreeFormDoorPanel
            shop={shop}
            doors={doors}
            setDoors={setDoors}
            selectedWallId={selectedWallId}
            setSelectedWallId={setSelectedWallId}
          />
        )}
      </div>
    </>
  );
}

// ═════════════════════════════════════════════════════════════════════
// FREE-FORM DOOR PANEL — sidebar UI for placing doors on free-form walls
// ═════════════════════════════════════════════════════════════════════
function FreeFormDoorPanel({ shop, doors, setDoors, selectedWallId, setSelectedWallId }) {
  // Drop orphan doors (wallId no longer in shop) — defensive, e.g. after re-edit
  const validDoors = doors.filter((d) =>
    shop.walls && shop.walls.some((w) => w.id === d.wallId)
  );
  const wallsList = shop.walls || [];
  const selectedWall = wallsList.find((w) => w.id === selectedWallId) || null;
  const selectedWallEnds = selectedWall ? getWallEndpoints(shop, selectedWall.id) : null;
  const doorsOnSelectedWall = selectedWall
    ? validDoors.filter((d) => d.wallId === selectedWall.id)
    : [];

  // Message shown when a door can't be placed (wall full or too short).
  const [doorWarning, setDoorWarning] = useState("");

  const addDoor = (type) => {
    if (selectedWallId === null) return;
    const newDoor = makeDoorOnWall(shop, selectedWallId, type, doors);
    if (!newDoor) {
      // No gap on this wall fits the opening — see makeDoorOnWall.
      setDoorWarning(
        `No room for ${type === "overhead" ? "a 10ft overhead" : "a 4ft walk"} door on this wall — remove or move one first.`
      );
      return;
    }
    setDoorWarning("");
    setDoors((prev) => [...prev, newDoor]);
  };
  const removeDoor = (id) => setDoors((prev) => prev.filter((d) => d.id !== id));

  return (
    <>
      {/* Wall picker — list of walls; user can also click on canvas to select */}
      <div
        style={{
          marginTop: 10,
          fontSize: 11,
          color: T.textMuted,
          marginBottom: 8,
          lineHeight: 1.4,
        }}
      >
        Click a wall in the canvas, or pick one from the list:
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(60px, 1fr))",
          gap: 4,
          marginBottom: 12,
        }}
      >
        {wallsList.map((w) => {
          const isActive = w.id === selectedWallId;
          const wallDoorCount = validDoors.filter((d) => d.wallId === w.id).length;
          const ends = getWallEndpoints(shop, w.id);
          return (
            <button
              key={w.id}
              onClick={() => setSelectedWallId(w.id)}
              style={{
                background: isActive ? T.blueSoft : "transparent",
                border: `1px solid ${isActive ? T.blue + "66" : "rgba(255,255,255,0.08)"}`,
                borderRadius: T.radiusSm,
                color: isActive ? T.blue : T.textDim,
                fontSize: 11,
                fontWeight: 600,
                padding: "5px 0",
                cursor: "pointer",
                fontFamily: "inherit",
                transition: "all .12s",
              }}
            >
              W{w.id + 1}
              {ends && <span style={{ fontSize: 11, opacity: 0.7, display: "block" }}>{ends.length.toFixed(0)}'</span>}
              {wallDoorCount > 0 && (
                <span style={{ fontSize: 11, opacity: 0.7, display: "block" }}>({wallDoorCount})</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Add door buttons — only enabled when a wall is selected */}
      {selectedWall ? (
        <>
          <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
            <button
              onClick={() => addDoor("door")}
              disabled={!selectedWallEnds || selectedWallEnds.length < 4}
              style={{ ...doorBtnS(ARCH.walkDoor), flex: 1, opacity: (!selectedWallEnds || selectedWallEnds.length < 4) ? 0.4 : 1 }}
            >
              + Walk Door
            </button>
            <button
              onClick={() => addDoor("overhead")}
              disabled={!selectedWallEnds || selectedWallEnds.length < 10}
              style={{ ...doorBtnS(ARCH.ovrDoor), flex: 1, opacity: (!selectedWallEnds || selectedWallEnds.length < 10) ? 0.4 : 1 }}
            >
              + Overhead
            </button>
          </div>

          {/* Shown only when the last add was refused for lack of space */}
          {doorWarning && (
            <div style={{ fontSize: 11, color: ARCH.warn, marginBottom: 8, lineHeight: 1.4 }}>
              {doorWarning}
            </div>
          )}

          {/* Door list for selected wall */}
          {doorsOnSelectedWall.length > 0 ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {doorsOnSelectedWall.map((d) => {
                const isOvr = d.type === "overhead";
                const clr = isOvr ? ARCH.ovrDoor : ARCH.walkDoor;
                return (
                  <div
                    key={d.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      background: `${clr}15`,
                      border: `1px solid ${clr}44`,
                      borderRadius: 4,
                      padding: "3px 8px",
                      fontSize: 11,
                      color: clr,
                    }}
                  >
                    {isOvr ? `OVR ${d.width}ft` : "WLK"}
                    <button
                      onClick={() => removeDoor(d.id)}
                      style={{
                        background: "none", border: "none", color: "inherit",
                        cursor: "pointer", fontSize: 11, padding: 0, opacity: 0.7,
                      }}
                    >
                      ✕
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ fontSize: 11, color: T.textMuted, fontStyle: "italic" }}>
              No doors on this wall yet. Drag doors on the floor plan to position.
            </div>
          )}
        </>
      ) : (
        <div style={{ fontSize: 11, color: T.textMuted, fontStyle: "italic" }}>
          Select a wall above to add doors.
        </div>
      )}
    </>
  );
}

// ═════════════════════════════════════════════════════════════════════
// RECTANGLE PREVIEW — original SVG preview, unchanged behavior
// ═════════════════════════════════════════════════════════════════════
function RectPreview({ shop, doors }) {
  // Cardinal label offset — scales with smaller building dimension so labels
  // stay close to walls regardless of aspect ratio.
  const minDim = Math.min(shop.w, shop.h);
  const cardOff = Math.max(1.5, Math.min(5, minDim * 0.15));

  return (
    <div
      style={{
        background: T.panelRaised,
        border: T.hairline,
        borderRadius: T.radiusLg,
        padding: 16,
        position: "relative",
        boxShadow: T.shadowMd,
        boxSizing: "border-box",
        width: "100%",
        height: "100%",
      }}
    >
      <svg
        width="100%"
        height="100%"
        viewBox={(() => {
          const dimFs = Math.max(2.4, cardOff * 0.7);
          const cardFs = Math.max(2.8, cardOff * 0.85);
          const dimText = cardOff * 0.6 + 0.5 + dimFs * 0.7 + 0.4;
          const pad = dimText + dimFs * 0.5 + cardFs * 0.5 + 1.5 + cardFs;
          return `${-pad} ${-pad} ${shop.w + pad * 2} ${shop.h + pad * 2}`;
        })()}
        preserveAspectRatio="xMidYMid meet"
        style={{ display: "block", overflow: "visible" }}
      >
        <defs>
          <pattern id="pg" width="5" height="5" patternUnits="userSpaceOnUse">
            <path d="M 5 0 L 0 0 0 5" fill="none" stroke={ARCH.gridLine} strokeWidth="0.3" />
          </pattern>
          <marker id="aH" markerWidth="4" markerHeight="4" refX="4" refY="2" orient="auto">
            <path d="M0,0 L4,2 L0,4" fill="none" stroke={ARCH.dim} strokeWidth="0.8" />
          </marker>
          <marker id="aT" markerWidth="4" markerHeight="4" refX="0" refY="2" orient="auto">
            <path d="M4,0 L0,2 L4,4" fill="none" stroke={ARCH.dim} strokeWidth="0.8" />
          </marker>
        </defs>

        {/* Floor + grid */}
        <rect x={0} y={0} width={shop.w} height={shop.h} fill={ARCH.floorBg} />
        <rect x={0} y={0} width={shop.w} height={shop.h} fill="url(#pg)" />

        {/* Walls */}
        <rect x={0} y={0} width={shop.w} height={shop.h} fill="none" stroke={ARCH.wall} strokeWidth="0.8" />

        {/* Doors — rendered using wall vectors so this works for both rect
            and free-form shops. Each door's offset along its wall is respected. */}
        {doors.map((d) => {
          const ends = getDoorEndpoints(d, shop);
          if (!ends) return null;
          const clr = d.type === "overhead" ? ARCH.ovrDoor : ARCH.walkDoor;
          // Label placement: midpoint, offset perpendicular to wall (outward)
          // We use the perpVec (rotated 90° CCW from wall direction) and pick
          // whichever side is OUTSIDE the polygon as the label position.
          // For a clockwise rectangle, the outside is to the LEFT of the wall
          // direction, which matches perpVec exactly.
          const labelOffsetFt = 1.5;
          const lx = ends.midpoint.x + ends.outwardVec.x * labelOffsetFt;
          const ly = ends.midpoint.y + ends.outwardVec.y * labelOffsetFt + 0.5;
          return (
            <g key={d.id}>
              <line
                x1={ends.start.x} y1={ends.start.y}
                x2={ends.end.x} y2={ends.end.y}
                stroke={ARCH.floorBg} strokeWidth="2.5"
              />
              <line
                x1={ends.start.x} y1={ends.start.y}
                x2={ends.end.x} y2={ends.end.y}
                stroke={clr} strokeWidth="1.5"
              />
              <text
                x={lx} y={ly}
                textAnchor="middle"
                fill={clr}
                fontSize="2"
                fontFamily="monospace"
              >
                {d.type === "overhead" ? "OVR" : "WLK"}
              </text>
            </g>
          );
        })}

        {/* Dimension annotations + cardinal markers */}
        {(() => {
          const dimFs = Math.max(2.4, cardOff * 0.7);
          const cardFs = Math.max(2.8, cardOff * 0.85);
          const dimTick = cardOff * 0.6;
          const dimArrow = dimTick + 0.5;
          const dimText = dimArrow + dimFs * 0.7 + 0.4;
          const cardLetter = dimText + dimFs * 0.5 + cardFs * 0.5 + 1.5;
          return (
            <>
              {/* Width dim (top) */}
              <line x1={0} y1={0} x2={0} y2={-dimTick} stroke={ARCH.dim} strokeWidth="0.3" strokeDasharray="1,1" opacity="0.5" />
              <line x1={shop.w} y1={0} x2={shop.w} y2={-dimTick} stroke={ARCH.dim} strokeWidth="0.3" strokeDasharray="1,1" opacity="0.5" />
              <line x1={0} y1={-dimArrow} x2={shop.w} y2={-dimArrow} stroke={ARCH.dim} strokeWidth="0.5" markerStart="url(#aT)" markerEnd="url(#aH)" />
              <text x={shop.w / 2} y={-dimText} textAnchor="middle" fill={ARCH.dim} fontSize={dimFs} fontFamily="monospace" fontWeight="700">
                {shop.w} {shop.unit.toUpperCase()}
              </text>
              {/* Height dim (right) */}
              <line x1={shop.w} y1={0} x2={shop.w + dimTick} y2={0} stroke={ARCH.dim} strokeWidth="0.3" strokeDasharray="1,1" opacity="0.5" />
              <line x1={shop.w} y1={shop.h} x2={shop.w + dimTick} y2={shop.h} stroke={ARCH.dim} strokeWidth="0.3" strokeDasharray="1,1" opacity="0.5" />
              <line x1={shop.w + dimArrow} y1={0} x2={shop.w + dimArrow} y2={shop.h} stroke={ARCH.dim} strokeWidth="0.5" markerStart="url(#aT)" markerEnd="url(#aH)" />
              <text
                x={shop.w + dimText}
                y={shop.h / 2}
                textAnchor="middle"
                fill={ARCH.dim}
                fontSize={dimFs}
                fontFamily="monospace"
                fontWeight="700"
                transform={`rotate(90, ${shop.w + dimText}, ${shop.h / 2})`}
              >
                {shop.h} {shop.unit.toUpperCase()}
              </text>
              {/* Cardinals */}
              <text x={shop.w / 2} y={-cardLetter} textAnchor="middle" fill={ARCH.north} fontSize={cardFs} fontFamily="monospace" fontWeight="700">N</text>
              <text x={shop.w / 2} y={shop.h + cardLetter + cardFs * 0.5} textAnchor="middle" fill={ARCH.cardinal} fontSize={cardFs} fontFamily="monospace" fontWeight="700">S</text>
              <text x={-cardLetter} y={shop.h / 2 + cardFs * 0.35} textAnchor="middle" fill={ARCH.cardinal} fontSize={cardFs} fontFamily="monospace" fontWeight="700">W</text>
              <text x={shop.w + cardLetter} y={shop.h / 2 + cardFs * 0.35} textAnchor="middle" fill={ARCH.cardinal} fontSize={cardFs} fontFamily="monospace" fontWeight="700">E</text>
            </>
          );
        })()}
      </svg>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// FREE-FORM CANVAS — interactive drawing surface
// ═════════════════════════════════════════════════════════════════════
function FreeFormCanvas({ shop, setShop, doors, setDoors, selectedWallId, setSelectedWallId, editMode, setEditMode }) {
  const svgRef = useRef(null);
  const [mousePos, setMousePos] = useState(null);     // shop coords {x, y}, null when off canvas
  const [shiftHeld, setShiftHeld] = useState(false);
  const [altHeld, setAltHeld] = useState(false);      // Alt = disable foot-snap for fine positioning
  const [dragVert, setDragVert] = useState(null);     // index of vertex being dragged
  const [lengthInput, setLengthInput] = useState(null); // {value: string} — null when not typing
  const lengthInputRef = useRef(null);
  // Mirror of lengthInput for synchronous reads from the keydown handler.
  // React state can lag behind fast keyrepeat; the ref is updated immediately
  // so the second digit of e.g. "20" is correctly seen as "panel already open".
  const lengthInputOpenRef = useRef(false);
  // Which wall is currently being hovered, for showing the ✕ delete button.
  // Tracked separately from selectedWallId (which is for door placement).
  // Active in two cases: (1) drawing mode, hover on placed walls; (2) edit mode,
  // hover on any wall of the closed polygon. NOT active in plain closed/non-edit mode.
  const [hoveredWallId, setHoveredWallId] = useState(null);
  // Toast message for door-blocking-delete and similar feedback. Cleared after timeout.
  const [errorMsg, setErrorMsg] = useState(null);
  const errorTimerRef = useRef(null);
  const showError = (msg) => {
    setErrorMsg(msg);
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    errorTimerRef.current = setTimeout(() => setErrorMsg(null), 2800);
  };

  // ─── Zoom/pan state ────────────────────────────────────────────────
  // The viewBox is the visible window into the drawing area, in shop coords.
  // Zoom in = smaller view.w/view.h. Pan = offset view.x/view.y.
  // Default centers the (DEFAULT_VIEW_W × DEFAULT_VIEW_H) window on the
  // top-left portion of the drawing area where users typically start.
  const [view, setView] = useState({
    x: -8, y: -8, w: DEFAULT_VIEW_W, h: DEFAULT_VIEW_H,
  });
  // Mirror for synchronous reads from native wheel/mousemove handlers.
  const viewRef = useRef(view);
  useEffect(() => { viewRef.current = view; }, [view]);

  // Pan drag state. Stored in a ref so mousemove handlers can read without re-render.
  // didPanRef stays true through the next click event so the click handler can skip
  // its place-vertex logic if the user just finished panning.
  const panDragRef = useRef(null);
  const didPanRef = useRef(false);

  // Current zoom level (relative to DEFAULT_VIEW_W). Used to scale hit tolerances.
  const zoom = DEFAULT_VIEW_W / view.w;

  const verts = shop.vertices || [];
  const closed = verts.length >= 3 && shop.walls && shop.walls.length === verts.length;

  // Quantize a coordinate to the foot-snap grid. Alt held = no snap (fine positioning).
  // Applied to mouse position before angle/alignment snap so all whole-foot inputs
  // produce whole-foot wall lengths on orthogonal walls.
  const quantizeToFoot = (p) => {
    if (altHeld) return p;
    return {
      x: Math.round(p.x / FOOT_SNAP) * FOOT_SNAP,
      y: Math.round(p.y / FOOT_SNAP) * FOOT_SNAP,
    };
  };

  // Convert a mouse event to shop coordinates (feet) using the SVG's viewBox transform
  const eventToShopCoords = (evt) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX;
    pt.y = evt.clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const transformed = pt.matrixTransform(ctm.inverse());
    return { x: transformed.x, y: transformed.y };
  };

  // ─── Zoom/pan helpers ──────────────────────────────────────────────
  // Clamp zoom by adjusting view.w/view.h around a focal point in shop coords.
  // Used by wheel zoom (focal = cursor) and zoom buttons (focal = view center).
  const zoomAround = (focalX, focalY, factor) => {
    const cur = viewRef.current;
    const curZoom = DEFAULT_VIEW_W / cur.w;
    const newZoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, curZoom * factor));
    if (newZoom === curZoom) return;
    const newW = DEFAULT_VIEW_W / newZoom;
    const newH = DEFAULT_VIEW_H / newZoom;
    // Keep the focal point at the same screen position before/after zoom:
    // its fractional position within the viewBox stays constant.
    const fx = (focalX - cur.x) / cur.w;
    const fy = (focalY - cur.y) / cur.h;
    const newX = focalX - fx * newW;
    const newY = focalY - fy * newH;
    const next = { x: newX, y: newY, w: newW, h: newH };
    viewRef.current = next;
    setView(next);
  };

  // Compute a viewBox that fits the current drawing (or the default if empty).
  // Adds FIT_PADDING_FT around the bbox so vertices/labels don't touch edges.
  const computeFitView = () => {
    if (!verts || verts.length === 0) {
      return { x: -8, y: -8, w: DEFAULT_VIEW_W, h: DEFAULT_VIEW_H };
    }
    const bbox = computeBbox(verts);
    const padW = bbox.w + FIT_PADDING_FT * 2;
    const padH = bbox.h + FIT_PADDING_FT * 2;
    // Match the SVG's aspect ratio so we don't introduce empty stripes.
    const targetAR = DEFAULT_VIEW_W / DEFAULT_VIEW_H;
    let w = padW, h = padH;
    if (padW / padH > targetAR) h = padW / targetAR;
    else                         w = padH * targetAR;
    return {
      x: bbox.minX + bbox.w / 2 - w / 2,
      y: bbox.minY + bbox.h / 2 - h / 2,
      w, h,
    };
  };

  const fitToDrawing = () => {
    const next = computeFitView();
    viewRef.current = next;
    setView(next);
  };

  const resetView = () => {
    const next = { x: -8, y: -8, w: DEFAULT_VIEW_W, h: DEFAULT_VIEW_H };
    viewRef.current = next;
    setView(next);
  };

  // Auto-fit on mount IF user is returning to a drawing they already made.
  // Empty deps array — fires exactly once when the component mounts.
  // (We accept the lint warning about missing deps; this is an intentional
  // "run on mount only" effect, not a "react to verts changes" effect.)
  useEffect(() => {
    if (verts && verts.length >= 3) fitToDrawing();
    // eslint-disable-next-line
  }, []);

  // ─── Wheel zoom ────────────────────────────────────────────────────
  // Native listener (not React onWheel) so we can preventDefault and stop
  // the page from scrolling. Centers the zoom on the cursor's shop position.
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const onWheel = (e) => {
      e.preventDefault();
      // Convert cursor to shop coords using current CTM (accounts for live viewBox)
      const pt = svg.createSVGPoint();
      pt.x = e.clientX; pt.y = e.clientY;
      const ctm = svg.getScreenCTM();
      if (!ctm) return;
      const focal = pt.matrixTransform(ctm.inverse());
      const factor = e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP;
      zoomAround(focal.x, focal.y, factor);
    };
    svg.addEventListener('wheel', onWheel, { passive: false });
    return () => svg.removeEventListener('wheel', onWheel);
  }, []);

  // Apply 0/45/90 angle snap relative to the previous wall direction.
  // refDir is the previous wall's direction vector; if null, snap relative to absolute axes.
  const applyAngleSnap = (from, to, refDir) => {
    if (shiftHeld) return to;
    const dx = to.x - from.x, dy = to.y - from.y;
    const len = Math.hypot(dx, dy);
    if (len < 0.1) return to;
    let absAngle = Math.atan2(dy, dx) * 180 / Math.PI;
    if (absAngle < 0) absAngle += 360;
    let refAngle = 0;
    if (refDir) {
      refAngle = Math.atan2(refDir.y, refDir.x) * 180 / Math.PI;
      if (refAngle < 0) refAngle += 360;
    }
    let bestSnap = null, bestDiff = Infinity;
    for (const rel of SNAP_ANGLES) {
      const target = (refAngle + rel) % 360;
      let diff = Math.abs(absAngle - target);
      if (diff > 180) diff = 360 - diff;
      if (diff < bestDiff) { bestDiff = diff; bestSnap = target; }
    }
    if (bestDiff <= ANGLE_SNAP_TOL) {
      const rad = bestSnap * Math.PI / 180;
      return { x: from.x + Math.cos(rad) * len, y: from.y + Math.sin(rad) * len };
    }
    return to;
  };

  // Apply alignment snap — checks if the candidate point shares X or Y with any
  // existing vertex. Returns adjusted point + which guides are active.
  // excludeIdx is the index of a vertex to ignore (used during drag so it doesn't snap to itself).
  const applyAlignmentSnap = (p, excludeIdx) => {
    let bestX = null, bestY = null;
    let bestXDist = ALIGN_TOL_FT, bestYDist = ALIGN_TOL_FT;
    for (let i = 0; i < verts.length; i++) {
      if (i === excludeIdx) continue;
      const v = verts[i];
      const dx = Math.abs(p.x - v.x);
      const dy = Math.abs(p.y - v.y);
      if (dx < bestXDist) { bestXDist = dx; bestX = v; }
      if (dy < bestYDist) { bestYDist = dy; bestY = v; }
    }
    const result = { x: p.x, y: p.y };
    const guides = [];
    if (bestX !== null) { result.x = bestX.x; guides.push({ type: "v", source: bestX }); }
    if (bestY !== null) { result.y = bestY.y; guides.push({ type: "h", source: bestY }); }
    return { point: result, guides };
  };

  // Combined snap for drawing: apply angle snap first, then alignment can override
  const snapForDraw = (from, to, refDir) => {
    const angled = applyAngleSnap(from, to, refDir);
    const aligned = applyAlignmentSnap(angled, null);
    if (aligned.guides.length > 0) return { point: aligned.point, guides: aligned.guides };
    return { point: angled, guides: [] };
  };

  // Helper: distance between two points (in shop units)
  const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

  // Find the wallId nearest to a point, or null if no wall is close enough.
  // Used in closed/edit mode to support "click a wall to select it for door placement".
  // Compute hit-test radius scaled to current zoom. When zoomed out, vertices
  // and walls would become tiny in screen pixels — boost the feet-radius so
  // they stay at least MIN_HIT_PX pixels wide regardless of zoom level.
  // The SVG width is roughly the canvas pixel width; ratio gives px-per-ft.
  const hitToleranceFt = (baseFt) => {
    const svg = svgRef.current;
    if (!svg) return baseFt;
    const pxW = svg.clientWidth || 800;
    const ftPerPx = viewRef.current.w / pxW;
    const minFt = MIN_HIT_PX * ftPerPx;
    return Math.max(baseFt, minFt);
  };

  const wallAt = (p, threshold = 1.5) => {
    if (!closed || !shop.walls) return null;
    const tol = hitToleranceFt(threshold);
    let bestId = null, bestDist = tol;
    for (const w of shop.walls) {
      const a = verts[w.startVertex], b = verts[w.endVertex];
      if (!a || !b) continue;
      // Distance from point p to segment AB
      const dx = b.x - a.x, dy = b.y - a.y;
      const len2 = dx * dx + dy * dy;
      if (len2 === 0) continue;
      let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
      t = Math.max(0, Math.min(1, t));
      const projX = a.x + dx * t, projY = a.y + dy * t;
      const d = Math.hypot(p.x - projX, p.y - projY);
      if (d < bestDist) { bestDist = d; bestId = w.id; }
    }
    return bestId;
  };

  // Helper: compute interior angle at vertex between prev->curr and curr->next
  const interiorAngle = (prev, curr, next) => {
    const v1x = prev.x - curr.x, v1y = prev.y - curr.y;
    const v2x = next.x - curr.x, v2y = next.y - curr.y;
    const dot = v1x * v2x + v1y * v2y;
    const m1 = Math.hypot(v1x, v1y), m2 = Math.hypot(v2x, v2y);
    if (m1 === 0 || m2 === 0) return 0;
    const c = Math.max(-1, Math.min(1, dot / (m1 * m2)));
    return Math.acos(c) * 180 / Math.PI;
  };

  // Track modifier keys + number key to open length input.
  // Alt = disable foot-snap (fine positioning). Shift = disable angle-snap.
  // Pressing 0-9 in draw mode (with at least 1 vertex) opens the type-a-length
  // panel so users can place precise wall lengths without mouse fiddling.
  useEffect(() => {
    const onDown = (e) => {
      if (e.key === "Shift") setShiftHeld(true);
      if (e.key === "Alt") { setAltHeld(true); e.preventDefault(); /* prevent browser menu focus */ }

      // F key fits the drawing into the canvas (mirrors the floating Fit button).
      // Only fires when not already typing in another input on the page.
      if ((e.key === "f" || e.key === "F") && !lengthInputOpenRef.current) {
        const tag = document.activeElement?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
        fitToDrawing();
        e.preventDefault();
        return;
      }

      // If the length input panel is already open, let the input handle digit
      // keys natively. We use a ref (not state) here because React state can
      // lag behind fast keyrepeat — the ref is updated synchronously below.
      if (lengthInputOpenRef.current) {
        if (e.key === "Escape") {
          lengthInputOpenRef.current = false;
          setLengthInput(null);
        }
        return;
      }

      // Open length input on first number key press during drawing
      if (/^[0-9]$/.test(e.key) && !closed && verts.length >= 1) {
        // Don't trigger if user is typing in some other input on the page
        const tag = document.activeElement?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
        lengthInputOpenRef.current = true;       // sync flag — protects against keyrepeat race
        setLengthInput({ value: e.key });
        e.preventDefault();
      }
    };
    const onUp = (e) => {
      if (e.key === "Shift") setShiftHeld(false);
      if (e.key === "Alt") setAltHeld(false);
    };
    window.addEventListener("keydown", onDown);
    window.addEventListener("keyup", onUp);
    return () => {
      window.removeEventListener("keydown", onDown);
      window.removeEventListener("keyup", onUp);
    };
  }, [closed, verts.length]);

  // When the length input opens, focus it and place cursor at the END of the
  // text (not select-all) so subsequent digits append rather than replace.
  // E.g. press "2" → panel opens with "2", press "0" → "20", press Enter → places 20ft wall.
  useEffect(() => {
    if (lengthInput !== null && lengthInputRef.current) {
      const inp = lengthInputRef.current;
      inp.focus();
      // Move cursor to end. setSelectionRange works for type=text but type=number
      // doesn't always support it cleanly — fall back to just focus if it throws.
      try {
        const len = inp.value.length;
        inp.setSelectionRange(len, len);
      } catch (err) { /* number inputs may not support selection — focus alone is fine */ }
    }
  }, [lengthInput]);

  // Place a wall of exact length in the current cursor direction.
  // Called when user submits the length input (Enter key).
  const placeWallByLength = (lengthFt) => {
    if (lengthFt <= 0 || verts.length === 0) return;
    const last = verts[verts.length - 1];

    // Determine direction: prefer current mouse position; fall back to extending the previous wall
    let dirX, dirY;
    if (mousePos) {
      // Use the snapped mouse direction so angle snap still applies
      let prevDir = null;
      if (verts.length >= 2) {
        const prev = verts[verts.length - 2];
        prevDir = { x: last.x - prev.x, y: last.y - prev.y };
      }
      const target = applyAngleSnap(last, mousePos, prevDir);
      const dx = target.x - last.x, dy = target.y - last.y;
      const len = Math.hypot(dx, dy);
      if (len < 0.1) {
        // Cursor on top of last vertex — fall back to extending previous wall direction
        if (prevDir) {
          const pl = Math.hypot(prevDir.x, prevDir.y);
          dirX = prevDir.x / pl; dirY = prevDir.y / pl;
        } else {
          dirX = 1; dirY = 0; // arbitrary default — east
        }
      } else {
        dirX = dx / len; dirY = dy / len;
      }
    } else if (verts.length >= 2) {
      // No mouse position — extend the previous wall's direction
      const prev = verts[verts.length - 2];
      const dx = last.x - prev.x, dy = last.y - prev.y;
      const len = Math.hypot(dx, dy);
      dirX = dx / len; dirY = dy / len;
    } else {
      // First wall, no mouse — default east
      dirX = 1; dirY = 0;
    }

    const newVert = { x: last.x + dirX * lengthFt, y: last.y + dirY * lengthFt };
    setShop((s) => {
      const newVerts = [...s.vertices, newVert];
      const bbox = computeBbox(newVerts);
      return { ...s, vertices: newVerts, w: Math.round(bbox.w * 10) / 10, h: Math.round(bbox.h * 10) / 10 };
    });
    lengthInputOpenRef.current = false;
    setLengthInput(null);
  };

  // Click on canvas — places a new vertex (draw mode) or closes the shape
  const handleClick = (evt) => {
    // Just finished a pan — consume one click and reset the flag
    if (didPanRef.current) {
      didPanRef.current = false;
      return;
    }
    if (dragVert !== null) return; // ignore click after drag

    // ─── Closed-shape click: select wall (and optionally add a door) ───
    // Behavior agreed on: clicking an empty wall adds a walk door at its midpoint
    // and selects that wall. Clicking a wall that already has doors just selects.
    // Users then drag doors on the floor plan to reposition.
    //
    // EDIT MODE EXCEPTION: don't auto-add doors when the user is in wall-edit
    // mode — they're trying to delete walls, not add doors. The hover-✕ badge
    // already handles wall clicks in edit mode (it intercepts via stopPropagation).
    if (closed) {
      if (editMode) return; // no door-add side effects during edit
      const p = eventToShopCoords(evt);
      const wid = wallAt(p);
      if (wid !== null) {
        if (setSelectedWallId) setSelectedWallId(wid);
        // Quick-add: if no door exists on this wall, add a walk door at midpoint
        const hasDoor = doors && doors.some((d) => d.wallId === wid);
        if (!hasDoor && setDoors) {
          const newDoor = makeDoorOnWall(shop, wid, "door", doors);
          if (newDoor) setDoors((prev) => [...prev, newDoor]);
        }
      }
      return;
    }

    // ─── Drawing mode click: place vertex (existing behavior) ───
    let p = quantizeToFoot(eventToShopCoords(evt));
    if (verts.length >= 3 && dist(p, verts[0]) < CLOSE_THRESHOLD_FT) {
      setShop((s) => {
        const newWalls = s.vertices.map((_, i) => ({
          id: i,
          startVertex: i,
          endVertex: (i + 1) % s.vertices.length,
          direction: null,
        }));
        const bbox = computeBbox(s.vertices);
        return { ...s, walls: newWalls, w: Math.round(bbox.w * 10) / 10, h: Math.round(bbox.h * 10) / 10 };
      });
      return;
    }
    if (verts.length >= 1) {
      const last = verts[verts.length - 1];
      let prevDir = null;
      if (verts.length >= 2) {
        const prev = verts[verts.length - 2];
        prevDir = { x: last.x - prev.x, y: last.y - prev.y };
      }
      const snapped = snapForDraw(last, p, prevDir);
      p = snapped.point;
    }
    setShop((s) => {
      const newVerts = [...s.vertices, p];
      const bbox = computeBbox(newVerts);
      return { ...s, vertices: newVerts, w: Math.round(bbox.w * 10) / 10, h: Math.round(bbox.h * 10) / 10 };
    });
  };

  // Mouse move — for preview line in draw mode, or for vertex drag in edit mode
  // Hit-test against the IMPLICIT walls during drawing mode, where shop.walls is
  // empty but visible walls exist between consecutive vertices (verts[i] → verts[i+1]).
  // Returns the wall index (== start vertex index) or null. Distinct from wallAt(),
  // which only works on closed shapes via shop.walls.
  const drawingWallAt = (p, threshold = 1.5) => {
    if (closed || verts.length < 2) return null;
    const tol = hitToleranceFt(threshold);
    let bestIdx = null, bestDist = tol;
    for (let i = 0; i < verts.length - 1; i++) {
      const a = verts[i], b = verts[i + 1];
      const dx = b.x - a.x, dy = b.y - a.y;
      const len2 = dx * dx + dy * dy;
      if (len2 === 0) continue;
      let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
      t = Math.max(0, Math.min(1, t));
      const projX = a.x + dx * t, projY = a.y + dy * t;
      const d = Math.hypot(p.x - projX, p.y - projY);
      if (d < bestDist) { bestDist = d; bestIdx = i; }
    }
    return bestIdx;
  };

  // ─── Wall deletion ─────────────────────────────────────────────────
  // Drawing-mode delete: wall index is the implicit-wall index (== start vertex).
  // Removing wall i means removing vertex (i+1). Polygon stays open and drawing
  // continues from the previous vertex (now the new last vertex).
  const deleteDrawingWall = (wallIndex) => {
    if (closed) return;
    const removeIdx = wallIndex + 1;
    if (removeIdx < 0 || removeIdx >= verts.length) return;
    setShop((s) => {
      const newVerts = s.vertices.slice();
      newVerts.splice(removeIdx, 1);
      const bbox = computeBbox(newVerts);
      return { ...s, vertices: newVerts, walls: [], w: Math.round(bbox.w * 10) / 10, h: Math.round(bbox.h * 10) / 10 };
    });
    setHoveredWallId(null);
  };

  // Edit-mode delete: wall is identified by its id (== its index in shop.walls).
  // Polygon was closed; after deletion it becomes open with one gap. Vertices
  // are rotated so the user can resume drawing from the vertex BEFORE the gap
  // back to the vertex AFTER the gap (which becomes the new starting vertex,
  // i.e., verts[0] — the close-shape handler always closes back to verts[0]).
  //
  // If THIS wall has a door, deletion is blocked (user must remove it first).
  // Doors on OTHER walls are preserved by remapping their wallIds: after the
  // rotation, walls will be re-numbered when the polygon re-closes; we
  // pre-compute each surviving door's NEW wallId so it lands on the right wall.
  const deleteEditModeWall = (wallId) => {
    if (!closed || !shop.walls) return;
    const wall = shop.walls.find((w) => w.id === wallId);
    if (!wall) return;
    // Block if THIS wall has a door — user must remove it first.
    // ALSO block if the NEXT wall has a door, because deleting wall N also
    // removes vertex N+1 which is the start of wall N+1 — that wall vanishes
    // too. Surprising the user with door loss on a non-deleted wall is bad UX.
    const nextWall = shop.walls.find((w) => w.startVertex === wall.endVertex);
    const blockingDoor = doors && doors.find((d) =>
      d.wallId === wall.id || (nextWall && d.wallId === nextWall.id)
    );
    if (blockingDoor) {
      showError("Remove the door from this wall (and the adjacent wall) before deleting it.");
      return;
    }

    const oldVerts = shop.vertices;
    const removeIdx = wall.endVertex;
    // Build new vertex order: start AFTER the deleted vertex, wrap around, end BEFORE.
    // Example: verts [A,B,C,D,E], delete C (idx 2): newVerts = [D,E,A,B].
    const startIdx = (removeIdx + 1) % oldVerts.length;
    const newVerts = [];
    // Track old→new index mapping so we can remap doors below.
    const oldToNew = new Array(oldVerts.length).fill(-1);
    for (let k = 0; k < oldVerts.length; k++) {
      const idx = (startIdx + k) % oldVerts.length;
      if (idx === removeIdx) continue;
      oldToNew[idx] = newVerts.length;
      newVerts.push(oldVerts[idx]);
    }
    // Remap each surviving door's wallId. After re-close, new wall i will
    // connect newVerts[i] → newVerts[(i+1) % N]. So a door whose old wall
    // connected old startVertex S and end E gets new wallId = oldToNew[S], but
    // ONLY if the new chain still has S → E as adjacent (which it does, since
    // the only adjacency we broke was the one at the deletion site).
    const remappedDoors = (doors || []).map((d) => {
      const oldWall = shop.walls.find((w) => w.id === d.wallId);
      if (!oldWall) return null;   // door was already orphaned — drop it
      const newStart = oldToNew[oldWall.startVertex];
      const newEnd = oldToNew[oldWall.endVertex];
      if (newStart < 0 || newEnd < 0) return null;
      // The new wallId of this door is the index where its start vertex sits in newVerts.
      // Sanity check: the next vertex in newVerts (modulo length) must be the door's end.
      // If not, the wall wasn't preserved by the rotation (shouldn't happen given we
      // already blocked deleting the wall this door is on, but be defensive).
      if ((newStart + 1) % newVerts.length !== newEnd) return null;
      return { ...d, wallId: newStart };
    }).filter(Boolean);

    setShop((s) => {
      const bbox = computeBbox(newVerts);
      return { ...s, vertices: newVerts, walls: [], w: Math.round(bbox.w * 10) / 10, h: Math.round(bbox.h * 10) / 10 };
    });
    if (setDoors) setDoors(remappedDoors);
    setHoveredWallId(null);
  };

  const handleMouseMove = (evt) => {
    // ─── Pan-drag: translate viewBox by client-pixel delta scaled to feet ───
    if (panDragRef.current) {
      const pd = panDragRef.current;
      const dxPx = evt.clientX - pd.startClientX;
      const dyPx = evt.clientY - pd.startClientY;
      const ftPerPxX = pd.startView.w / pd.rectW;
      const ftPerPxY = pd.startView.h / pd.rectH;
      const next = {
        x: pd.startView.x - dxPx * ftPerPxX,
        y: pd.startView.y - dyPx * ftPerPxY,
        w: pd.startView.w,
        h: pd.startView.h,
      };
      viewRef.current = next;
      setView(next);
      if (Math.abs(dxPx) > 2 || Math.abs(dyPx) > 2) didPanRef.current = true;
      return;
    }
    // Foot-snap mouse position before all other logic.
    const p = quantizeToFoot(eventToShopCoords(evt));
    if (dragVert !== null) {
      // Drag vertex — apply alignment snap (no angle snap during drag — feels weird)
      const snapped = applyAlignmentSnap(p, dragVert);
      setShop((s) => {
        const newVerts = s.vertices.slice();
        newVerts[dragVert] = snapped.point;
        const bbox = computeBbox(newVerts);
        return { ...s, vertices: newVerts, w: Math.round(bbox.w * 10) / 10, h: Math.round(bbox.h * 10) / 10 };
      });
      setMousePos(snapped.point);
      return;
    }

    // Track which wall (if any) is being hovered, for the ✕ delete badge.
    // Active in drawing mode (always — placed walls can be deleted) and in
    // edit mode (closed shape but user is in wall-edit). NOT active in plain
    // closed mode where wall-click means "add a door".
    const rawP = eventToShopCoords(evt);   // unsnapped — UX feels off if hover snaps too
    let hoverWid = null;
    if (!closed) {
      hoverWid = drawingWallAt(rawP);
    } else if (editMode) {
      hoverWid = wallAt(rawP);
    }
    if (hoverWid !== hoveredWallId) setHoveredWallId(hoverWid);

    if (!closed) setMousePos(p);
  };

  const handleMouseDown = (evt) => {
    // ─── Pan-drag entry: middle-click OR Alt+left-click ───
    // Matches CadCanvas convention exactly. didPanRef is set true so the
    // subsequent click event can be ignored (otherwise releasing a pan would
    // place a vertex or select a wall depending on mode).
    if (evt.button === 1 || (evt.button === 0 && evt.altKey)) {
      evt.preventDefault();
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return;
      panDragRef.current = {
        startClientX: evt.clientX,
        startClientY: evt.clientY,
        startView: { ...viewRef.current },
        rectW: rect.width,
        rectH: rect.height,
      };
      didPanRef.current = false; // becomes true on first move
      return;
    }
    if (!closed) return;
    // In closed/edit mode, see if user clicked on a vertex
    const p = eventToShopCoords(evt);
    const tol = hitToleranceFt(VERTEX_HIT_FT);
    for (let i = 0; i < verts.length; i++) {
      if (dist(p, verts[i]) <= tol) {
        setDragVert(i);
        evt.preventDefault();
        return;
      }
    }
  };

  const handleMouseUp = () => {
    if (panDragRef.current) {
      panDragRef.current = null;
      // didPanRef stays true through the next click handler so it can short-circuit
      return;
    }
    if (dragVert !== null) setDragVert(null);
  };

  const handleMouseLeave = () => {
    setMousePos(null);
    setHoveredWallId(null);
    if (dragVert !== null) setDragVert(null);
    panDragRef.current = null;
  };

  // ─── Compute preview line + alignment guides for current mouse position ───
  let previewData = null;
  if (!closed && mousePos && verts.length >= 1) {
    const last = verts[verts.length - 1];
    let prevDir = null;
    if (verts.length >= 2) {
      const prev = verts[verts.length - 2];
      prevDir = { x: last.x - prev.x, y: last.y - prev.y };
    }
    const snapped = snapForDraw(last, mousePos, prevDir);
    previewData = { from: last, to: snapped.point, guides: snapped.guides, prevDir, prev: verts[verts.length - 2] };
  }

  // Alignment guides for vertex drag mode
  let dragGuides = [];
  if (dragVert !== null && mousePos) {
    const aligned = applyAlignmentSnap(mousePos, dragVert);
    dragGuides = aligned.guides;
  }

  // ─── Compute viewBox from zoom/pan state ───
  // The viewBox is no longer a fixed window — it's controlled by zoom/pan.
  // The container has overflow:hidden so anything past the viewBox edges is
  // clipped at the canvas border, not leaked onto the page.
  const PAD = 8;
  const viewBox = `${view.x} ${view.y} ${view.w} ${view.h}`;
  // Floor + grid sized to the current view exactly. Container clipping handles
  // the rest. No need to extend beyond the viewBox.
  const bgX = view.x;
  const bgY = view.y;
  const bgW = view.w;
  const bgH = view.h;

  return (
    <div
      style={{
        background: T.panelRaised,
        border: T.hairline,
        borderRadius: T.radiusLg,
        padding: 16,
        position: "relative",
        boxShadow: T.shadowMd,
        boxSizing: "border-box",
        width: "100%",
        height: "100%",
        overflow: "hidden",   // Clip grid/floor to canvas bounds during zoom/pan
      }}
    >
      {/* ─── Floating zoom controls (top-left of canvas) ───
          Mirrors the main CadCanvas convention: wheel zooms, middle/Alt-drag pans.
          Buttons here for users who don't know the shortcuts. */}
      <div
        style={{
          position: "absolute",
          top: 22,
          left: 22,
          display: "flex",
          gap: 4,
          zIndex: 6,
        }}
      >
        <ZoomBtn title="Zoom in (or scroll wheel up)" onClick={() => {
          const cx = viewRef.current.x + viewRef.current.w / 2;
          const cy = viewRef.current.y + viewRef.current.h / 2;
          zoomAround(cx, cy, ZOOM_STEP * ZOOM_STEP); // 2 ticks per click feels snappier
        }}>+</ZoomBtn>
        <ZoomBtn title="Zoom out (or scroll wheel down)" onClick={() => {
          const cx = viewRef.current.x + viewRef.current.w / 2;
          const cy = viewRef.current.y + viewRef.current.h / 2;
          zoomAround(cx, cy, 1 / (ZOOM_STEP * ZOOM_STEP));
        }}>−</ZoomBtn>
        <ZoomBtn title="Fit drawing to canvas (F)" onClick={fitToDrawing} wide>Fit</ZoomBtn>
        <ZoomBtn title="Reset view" onClick={resetView} wide>Reset</ZoomBtn>
        <div style={{
          padding: "4px 8px",
          fontSize: 11,
          color: T.textMuted,
          background: T.panelRaised,
          border: T.hairline,
          borderRadius: T.radiusSm,
          fontFamily: "monospace",
          alignSelf: "center",
        }}>
          {Math.round(zoom * 100)}%
        </div>
      </div>

      {/* Error/info toast — appears center-bottom when an action is blocked
          or info needs to be communicated (e.g. "remove door before deleting wall"). */}
      {errorMsg && (
        <div
          style={{
            position: "absolute",
            bottom: 60,
            left: "50%",
            transform: "translateX(-50%)",
            background: `${ARCH.warn}`,
            color: "#fff",
            padding: "8px 14px",
            borderRadius: T.radiusSm,
            fontSize: 11,
            fontWeight: 600,
            boxShadow: T.shadowMd,
            pointerEvents: "none",
            userSelect: "none",
            zIndex: 8,
            maxWidth: "80%",
            textAlign: "center",
          }}
        >
          {errorMsg}
        </div>
      )}

      {/* Modifier hint pill — bottom-left, only visible while drawing */}
      {!closed && (
        <div
          style={{
            position: "absolute",
            bottom: 22,
            left: 22,
            display: "flex",
            gap: 6,
            fontSize: 11,
            color: T.textMuted,
            pointerEvents: "none",
            userSelect: "none",
            zIndex: 5,
          }}
        >
          <KbdHint label="Type number" desc="exact length" active={lengthInput !== null} />
          <KbdHint label="Shift" desc="free angle" active={shiftHeld} />
          <KbdHint label="Alt" desc="fine snap" active={altHeld} />
        </div>
      )}

      {/* Type-a-length input — top-right, opens when user presses 0-9 */}
      {/* Lets the user place a wall at an exact length without mouse precision. */}
      {/* Direction comes from the current cursor position (snapped to angle). */}
      {lengthInput !== null && (
        <div
          style={{
            position: "absolute",
            top: 22,
            right: 22,
            background: T.panelRaised,
            border: `1px solid ${T.blue}66`,
            borderRadius: T.radiusSm,
            padding: "10px 12px",
            display: "flex",
            flexDirection: "column",
            gap: 6,
            boxShadow: T.shadowMd,
            zIndex: 10,
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 600, color: T.blue, letterSpacing: ".05em", textTransform: "uppercase" }}>
            Wall length
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input
              ref={lengthInputRef}
              type="number"
              step="0.1"
              min="0.1"
              value={lengthInput.value}
              onChange={(e) => setLengthInput({ value: e.target.value })}
              onKeyDown={(e) => {
                // Submit on Enter — places wall at typed length in current cursor direction
                if (e.key === "Enter") {
                  const val = parseFloat(lengthInput.value);
                  if (!isNaN(val) && val > 0) placeWallByLength(val);
                  e.stopPropagation();
                }
                // Escape closes the panel
                if (e.key === "Escape") {
                  lengthInputOpenRef.current = false;
                  setLengthInput(null);
                  e.stopPropagation();
                }
              }}
              style={{
                ...inputS,
                width: 80,
                padding: "6px 8px",
                fontSize: 13,
                fontWeight: 600,
              }}
              autoFocus
            />
            <span style={{ color: T.textDim, fontSize: 11 }}>{shop.unit}</span>
          </div>
          <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>
            Enter to place • Esc to cancel
          </div>
        </div>
      )}

      <svg
        ref={svgRef}
        width="100%"
        height="100%"
        viewBox={viewBox}
        preserveAspectRatio="xMidYMid meet"
        style={{
          display: "block",
          // Keep visible — the parent container has overflow:hidden which does
          // the actual clipping. SVG `overflow: hidden` causes the element to
          // collapse to its intrinsic viewBox size in flex containers, breaking
          // layout. Visible keeps the SVG sized by its CSS width/height (100%).
          overflow: "visible",
          cursor: closed ? "default" : "crosshair",
          userSelect: "none",
        }}
        onClick={handleClick}
        onMouseMove={handleMouseMove}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        <defs>
          <pattern id="pgFree" width="5" height="5" patternUnits="userSpaceOnUse">
            <path d="M 5 0 L 0 0 0 5" fill="none" stroke={ARCH.gridLine} strokeWidth="0.3" />
          </pattern>
        </defs>

        {/* Floor + grid — sized to cover the current view AND the full drawing
            area, so users see the grid wherever they pan/zoom to. */}
        <rect x={bgX} y={bgY} width={bgW} height={bgH} fill={ARCH.floorBg} />
        <rect x={bgX} y={bgY} width={bgW} height={bgH} fill="url(#pgFree)" />

        {/* Alignment guides — render BEFORE walls so they sit underneath.
            Lines extend across the visible viewBox in each direction. */}
        {(previewData?.guides || dragGuides).map((g, i) => (
          <g key={`guide-${i}`} pointerEvents="none">
            {g.type === "v" ? (
              <line x1={g.source.x} y1={view.y} x2={g.source.x} y2={view.y + view.h} stroke={ARCH.guide} strokeWidth="0.3" strokeDasharray="1.5,1" opacity="0.7" />
            ) : (
              <line x1={view.x} y1={g.source.y} x2={view.x + view.w} y2={g.source.y} stroke={ARCH.guide} strokeWidth="0.3" strokeDasharray="1.5,1" opacity="0.7" />
            )}
            {/* Highlight the source vertex */}
            <circle cx={g.source.x} cy={g.source.y} r="1.8" fill="none" stroke={ARCH.guide} strokeWidth="0.4" />
          </g>
        ))}

        {/* Walls — selected wall (when shape is closed) renders thicker + brighter
            so users can see which wall they clicked. The wallId of each wall in
            shop.walls aligns with the index here when shape is closed. */}
        {verts.length >= 2 && verts.map((v, i) => {
          if (i === verts.length - 1 && !closed) return null;
          const a = v;
          const b = verts[(i + 1) % verts.length];
          const wallId = closed && shop.walls?.[i]?.id;
          const isSelected = closed && wallId === selectedWallId;
          return (
            <line
              key={`wall-${i}`}
              x1={a.x} y1={a.y} x2={b.x} y2={b.y}
              stroke={isSelected ? "#4d9fff" : ARCH.wall}
              strokeWidth={isSelected ? "1.2" : "0.7"}
              strokeLinecap="round"
              style={{ cursor: closed ? "pointer" : "default" }}
            />
          );
        })}

        {/* Wall length labels — only for placed walls */}
        {verts.length >= 2 && verts.map((v, i) => {
          if (i === verts.length - 1 && !closed) return null;
          const a = v;
          const b = verts[(i + 1) % verts.length];
          const mx = (a.x + b.x) / 2;
          const my = (a.y + b.y) / 2;
          const dx = b.x - a.x, dy = b.y - a.y;
          const wl = Math.hypot(dx, dy);
          if (wl < 0.5) return null;
          // Offset label perpendicular to wall
          const perpX = -dy / wl * 1.5;
          const perpY = dx / wl * 1.5;
          return (
            <text
              key={`lbl-${i}`}
              x={mx + perpX}
              y={my + perpY + 0.7}
              textAnchor="middle"
              fill={ARCH.dim}
              fontSize="2"
              fontFamily="monospace"
              fontWeight="700"
              pointerEvents="none"
            >
              {wl.toFixed(1)}'
            </text>
          );
        })}

        {/* ─── Hover ✕ delete badge for the wall under the cursor ───
            Drawing mode (open polygon): hovered wall index === hoveredWallId
            Edit mode (closed polygon): hovered wall.id === hoveredWallId
            The ✕ is positioned at wall midpoint offset slightly toward the
            INSIDE of the polygon (or any side in drawing mode) so it doesn't
            collide with the wall length label outside. */}
        {hoveredWallId !== null && (() => {
          // Find the wall's two endpoints for both drawing and edit modes
          let a, b, wallIdForDelete = null;
          if (closed && editMode) {
            const w = shop.walls?.find((wl) => wl.id === hoveredWallId);
            if (!w) return null;
            a = verts[w.startVertex]; b = verts[w.endVertex];
            wallIdForDelete = w.id;
          } else if (!closed) {
            // Drawing mode: hoveredWallId is the start-vertex index
            const i = hoveredWallId;
            if (i < 0 || i >= verts.length - 1) return null;
            a = verts[i]; b = verts[i + 1];
            wallIdForDelete = i; // not really an id, just the index for deleteDrawingWall
          } else {
            return null;
          }
          if (!a || !b) return null;
          const mx = (a.x + b.x) / 2;
          const my = (a.y + b.y) / 2;
          const dx = b.x - a.x, dy = b.y - a.y;
          const wl = Math.hypot(dx, dy);
          if (wl < 1) return null; // wall too short to fit the badge meaningfully
          // Place the ✕ on the OPPOSITE side from the length label so they don't overlap.
          // Length label uses perpVec (-dy/wl, dx/wl) * 1.5 — we use the negated direction.
          const perpX = dy / wl * 2.2;
          const perpY = -dx / wl * 2.2;
          const cx = mx + perpX;
          const cy = my + perpY;
          // Scale icon size with zoom so it stays visually consistent.
          // Base size is in feet; clamp so it doesn't get TOO huge or TOO tiny.
          const r = Math.max(1.2, Math.min(2.5, 18 * (viewRef.current.w / 800)));
          return (
            <g
              key="wall-delete-badge"
              style={{ cursor: "pointer" }}
              onMouseDown={(e) => { e.stopPropagation(); }}
              onClick={(e) => {
                e.stopPropagation();
                if (closed && editMode) {
                  deleteEditModeWall(wallIdForDelete);
                } else if (!closed) {
                  deleteDrawingWall(wallIdForDelete);
                }
              }}
            >
              <circle cx={cx} cy={cy} r={r} fill={ARCH.warn} stroke="#fff" strokeWidth="0.3" />
              <text
                x={cx}
                y={cy + r * 0.35}
                textAnchor="middle"
                fill="#fff"
                fontSize={r * 1.25}
                fontFamily="sans-serif"
                fontWeight="700"
                pointerEvents="none"
              >
                ✕
              </text>
            </g>
          );
        })()}

        {/* Doors — only render when shape is closed. Doors use getDoorEndpoints()
            so they sit correctly on any wall (axis-aligned or angled). */}
        {closed && doors && doors.map((d) => {
          const ends = getDoorEndpoints(d, shop);
          if (!ends) return null;
          const clr = d.type === "overhead" ? ARCH.ovrDoor : ARCH.walkDoor;
          // Label sits perpendicular to wall, on the outside (which is -perpVec
          // for clockwise polygons — same convention as RectPreview).
          const labelOffsetFt = 1.5;
          const lx = ends.midpoint.x + ends.outwardVec.x * labelOffsetFt;
          const ly = ends.midpoint.y + ends.outwardVec.y * labelOffsetFt + 0.5;
          return (
            <g key={`door-${d.id}`} pointerEvents="none">
              {/* Knock out the wall behind the door so it reads as an opening */}
              <line
                x1={ends.start.x} y1={ends.start.y}
                x2={ends.end.x} y2={ends.end.y}
                stroke={ARCH.floorBg} strokeWidth="1.4"
              />
              <line
                x1={ends.start.x} y1={ends.start.y}
                x2={ends.end.x} y2={ends.end.y}
                stroke={clr} strokeWidth="0.9"
              />
              <text
                x={lx} y={ly}
                textAnchor="middle"
                fill={clr}
                fontSize="1.6"
                fontFamily="monospace"
                fontWeight="700"
              >
                {d.type === "overhead" ? "OVR" : "WLK"}
              </text>
            </g>
          );
        })}

        {/* Preview line + length + angle indicator (draw mode) */}
        {previewData && (
          <g pointerEvents="none">
            <line
              x1={previewData.from.x} y1={previewData.from.y}
              x2={previewData.to.x} y2={previewData.to.y}
              stroke={shiftHeld ? ARCH.freeAng : ARCH.preview}
              strokeWidth="0.5"
              strokeDasharray="1,0.8"
              opacity="0.7"
            />
            {/* Live length */}
            <text
              x={(previewData.from.x + previewData.to.x) / 2 + 1.5}
              y={(previewData.from.y + previewData.to.y) / 2 - 0.8}
              fill={T.textDim}
              fontSize="1.8"
              fontFamily="monospace"
            >
              {dist(previewData.from, previewData.to).toFixed(1)}'
            </text>
            {/* Angle indicator — only when there's a previous wall */}
            {previewData.prevDir && previewData.prev && (() => {
              const last = previewData.from;
              const prev = previewData.prev;
              const target = previewData.to;
              const angleVal = interiorAngle(prev, last, target);
              const r = 3;
              const a1 = Math.atan2(prev.y - last.y, prev.x - last.x);
              const a2 = Math.atan2(target.y - last.y, target.x - last.x);
              let sweep = a2 - a1;
              while (sweep > Math.PI) sweep -= 2 * Math.PI;
              while (sweep < -Math.PI) sweep += 2 * Math.PI;
              const sweepFlag = sweep > 0 ? 1 : 0;
              const sx = last.x + Math.cos(a1) * r;
              const sy = last.y + Math.sin(a1) * r;
              const ex = last.x + Math.cos(a2) * r;
              const ey = last.y + Math.sin(a2) * r;
              const bisectA = a1 + sweep / 2;
              const tx = last.x + Math.cos(bisectA) * (r + 2);
              const ty = last.y + Math.sin(bisectA) * (r + 2);
              return (
                <g>
                  <path
                    d={`M ${sx} ${sy} A ${r} ${r} 0 0 ${sweepFlag} ${ex} ${ey}`}
                    fill="none" stroke={ARCH.ovrDoor} strokeWidth="0.3"
                  />
                  <text
                    x={tx} y={ty + 0.6}
                    textAnchor="middle"
                    fill={ARCH.ovrDoor}
                    fontSize="1.8"
                    fontFamily="monospace"
                    fontWeight="700"
                  >
                    {angleVal.toFixed(0)}°
                  </text>
                </g>
              );
            })()}
          </g>
        )}

        {/* Vertices — start vertex is green and bigger when shape can be closed */}
        {verts.map((v, i) => {
          const isStart = i === 0 && !closed && verts.length >= 3;
          const isDragging = dragVert === i;
          const r = isStart ? 1.5 : (isDragging ? 1.4 : (closed ? 1.1 : 1.0));
          const fill = isStart ? ARCH.vStart : (isDragging ? ARCH.vSelect : ARCH.vertex);
          return (
            <circle
              key={`v-${i}`}
              cx={v.x} cy={v.y} r={r}
              fill={fill}
              stroke="#fff"
              strokeWidth="0.3"
              style={{ cursor: closed ? "grab" : "default" }}
            />
          );
        })}

        {/* Empty-state hint — centered on the current view so it's always visible */}
        {verts.length === 0 && (
          <text
            x={view.x + view.w / 2}
            y={view.y + view.h / 2}
            textAnchor="middle"
            fill={T.textMuted}
            fontSize="3"
            fontFamily="monospace"
            opacity="0.5"
            pointerEvents="none"
          >
            Click to start drawing
          </text>
        )}
      </svg>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// OPEN FLOOR PLAN BUTTON — disabled when free-form shape is invalid
// ═════════════════════════════════════════════════════════════════════
// ─── Helper components ─────────────────────────────────────────

// One template choice: a mini plan drawn from the template's own data
// (shop outline, equipment footprints, doors) so the thumbnail is accurate,
// plus its name and size.
function TemplateCard({ template, onPick }) {
  const { shop, equipment = [], doors = [], office } = template;
  const BOX_W = 44, BOX_H = 34;                                  // thumbnail box (px)
  const k = Math.min(BOX_W / shop.w, BOX_H / shop.h);            // ft → px
  const w = shop.w * k, h = shop.h * k;
  const items = equipment
    .map((e) => ({ e, cat: CATALOG.find((c) => c.id === e.id) }))
    .filter((x) => x.cat);
  // Door position along its wall → a short tick on the outline
  const doorTick = (d) => {
    const len = (d.width || 4) * k;
    if (d.wall === "N" || d.wall === "S") {
      const x = d.pos * w - len / 2;
      return { x, y: d.wall === "N" ? -1 : h - 1, w: len, h: 2 };
    }
    const y = d.pos * h - len / 2;
    return { x: d.wall === "W" ? -1 : w - 1, y, w: 2, h: len };
  };
  const itemCount = equipment.length + (office ? 1 : 0);
  return (
    <button
      onClick={onPick}
      title={template.description}
      style={{
        display: "flex", alignItems: "center", gap: 8, textAlign: "left",
        background: T.surface, border: T.hairlineHi, borderRadius: 8,
        padding: "8px 8px", color: T.text, fontFamily: "inherit", cursor: "pointer", minWidth: 0,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = T.blue)}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = "")}
    >
      <svg width={BOX_W} height={BOX_H} viewBox={`-2 -2 ${BOX_W + 4} ${BOX_H + 4}`} aria-hidden="true" style={{ flexShrink: 0 }}>
        <rect x={0} y={0} width={w} height={h} fill="none" stroke={ARCH.wall} strokeWidth={1.5} />
        {office && (
          <rect x={0} y={0} width={office.w * k} height={office.h * k} fill="rgba(255,229,0,.12)" stroke="#ffe500" strokeWidth={0.8} />
        )}
        {items.map(({ e, cat }, i) => {
          const rot = ((e.rot || 0) % 180) !== 0;
          const fw = (rot ? cat.fh : cat.fw) * (e.scaleX || 1) * k;
          const fh = (rot ? cat.fw : cat.fh) * (e.scaleY || 1) * k;
          return <rect key={i} x={e.cx * k - fw / 2} y={e.cy * k - fh / 2} width={fw} height={fh} fill="none" stroke="#ff9900" strokeWidth={0.8} />;
        })}
        {doors.map((d, i) => {
          const r = doorTick(d);
          return <rect key={`d${i}`} {...r} fill={d.type === "overhead" ? ARCH.ovrDoor : ARCH.walkDoor} />;
        })}
      </svg>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.25 }}>{template.name}</div>
        <div style={{ fontSize: 12, color: T.textDim, whiteSpace: "nowrap" }}>
          {shop.w}×{shop.h} ft · {itemCount} items
        </div>
      </div>
    </button>
  );
}

// Width/Depth field for rectangle shops. Keeps its own text while typing so
// the user can clear the box and type a new number — the old version wrote
// every keystroke straight to the shop, so clearing it made a 0 ft shop.
// Valid values (10–300 ft) apply live; anything else snaps into range on blur.
const DIM_MIN = 10;
const DIM_MAX = 300;
function DimInput({ value, onCommit }) {
  const [draft, setDraft] = useState(String(value));
  // Stay in sync when the shop changes from elsewhere (load, template, AI import)
  useEffect(() => { setDraft(String(value)); }, [value]);

  const handleChange = (e) => {
    const text = e.target.value;
    setDraft(text);
    const n = Number(text);
    if (text !== "" && n >= DIM_MIN && n <= DIM_MAX) onCommit(n);
  };
  const handleBlur = () => {
    const n = Number(draft);
    // Empty / non-number → restore last good value; out of range → clamp
    const fixed = draft === "" || isNaN(n) ? value : Math.min(DIM_MAX, Math.max(DIM_MIN, n));
    setDraft(String(fixed));
    if (fixed !== value) onCommit(fixed);
  };
  const bad = draft === "" || isNaN(Number(draft)) || Number(draft) < DIM_MIN || Number(draft) > DIM_MAX;

  return (
    <input
      type="number"
      min={DIM_MIN}
      max={DIM_MAX}
      value={draft}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      title={`${DIM_MIN}–${DIM_MAX} ft`}
      style={{ ...inputS, border: bad ? `1px solid ${ARCH.warn}` : inputS.border }}
    />
  );
}
function SectionLabel({ children }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: T.blue,
        letterSpacing: ".1em",
        textTransform: "uppercase",
      }}
    >
      {children}
    </div>
  );
}

function FieldLabel({ children }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: T.textDim,
        letterSpacing: ".06em",
        textTransform: "uppercase",
        marginBottom: 5,
      }}
    >
      {children}
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div
      style={{
        background: T.surface,
        border: T.hairline,
        borderRadius: T.radiusSm,
        padding: "8px 6px",
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: 11, color: T.textDim, letterSpacing: ".05em", textTransform: "uppercase" }}>
        {label}
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginTop: 2 }}>
        {value}
      </div>
    </div>
  );
}

// Small keyboard hint pill — shown in the corner of the drawing canvas.
// Lights up when the corresponding key is currently held so users can see
// at a glance what mode they're in.
function KbdHint({ label, desc, active }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 4,
        padding: "3px 7px",
        background: active ? `${T.blue}22` : "rgba(255,255,255,0.04)",
        border: `1px solid ${active ? T.blue + "66" : "rgba(255,255,255,0.06)"}`,
        borderRadius: T.radiusSm,
        color: active ? T.blue : T.textMuted,
        transition: "all .12s",
      }}
    >
      <span style={{ fontWeight: 600, fontSize: 11, fontFamily: "monospace" }}>{label}</span>
      <span style={{ fontSize: 11, opacity: 0.8 }}>{desc}</span>
    </div>
  );
}

// Floating zoom-control button. Plain button with hover affordance — doesn't
// pull from the theme tokens beyond panelRaised/text/blue so it stays readable
// against any canvas background.
function ZoomBtn({ children, onClick, title, wide }) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        background: T.panelRaised,
        border: T.hairline,
        borderRadius: T.radiusSm,
        color: T.text,
        padding: wide ? "4px 10px" : "4px 8px",
        minWidth: wide ? 0 : 26,
        fontSize: wide ? 10 : 14,
        fontWeight: 600,
        fontFamily: "inherit",
        cursor: "pointer",
        lineHeight: 1.2,
      }}
    >
      {children}
    </button>
  );
}

// ─── Local style objects ───────────────────────────────────────
const inputS = {
  width: "100%",
  background: T.surface,
  border: T.hairlineHi,
  borderRadius: T.radiusSm,
  color: T.text,
  padding: "8px 10px",
  fontSize: 12,
  fontFamily: "inherit",
  outline: "none",
  boxSizing: "border-box",
};

const doorBtnS = (color) => ({
  background: `${color}15`,
  border: `1px solid ${color}44`,
  borderRadius: 5,
  color,
  fontSize: 11,
  fontWeight: 600,
  padding: "6px 12px",
  cursor: "pointer",
  fontFamily: "inherit",
  transition: "background .12s",
});

// Shape mode toggle button — active state uses the blue accent
const shapeBtnS = (active) => ({
  flex: 1,
  background: active ? T.blueSoft : "transparent",
  border: `1px solid ${active ? T.blue + "66" : "rgba(255,255,255,0.08)"}`,
  borderRadius: T.radiusSm,
  color: active ? T.blue : T.textDim,
  fontSize: 12,
  fontWeight: 600,
  padding: "10px 0",
  cursor: "pointer",
  fontFamily: "inherit",
  letterSpacing: ".03em",
  transition: "all .12s",
});

// Generic secondary button — used for undo/reset in the free-form panel
const secondaryBtnS = {
  background: "transparent",
  border: T.hairlineHi,
  borderRadius: T.radiusSm,
  color: T.textDim,
  fontSize: 11,
  fontWeight: 600,
  padding: "8px 12px",
  cursor: "pointer",
  fontFamily: "inherit",
  transition: "all .12s",
};
